import { Button, Tooltip } from "@jhool-io/fe-components";
import { useState } from "react";
import patternBg from "../../assets/images/patternbg.png";
import SideNavLink from "./SideNavLink/SideNavLink";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { toggleNavState } from "../../redux/features/nav/navSlice";
import {
    cn,
    getClientDisplayName,
    getClientInitials,
    getSessionsNeedingConsent,
    truncateString,
} from "../../utils/helpers";
import {
    useFetchUserDetails,
    useFetchUserPractices,
} from "../../modules/auth/hooks/auth.queries";
import { IPractice } from "../../modules/auth/types/auth.types";
import Avatar from "../Avatar/Avatar";
import Skeleton from "../Skeleton/Skeleton";
import useAuthContext from "../../hooks/useAuthContext";
import RecordsIcon from "../Icons/SideNav/Records";
import useCurrentClientProfile from "../../hooks/useCurrentClient";
import { handleChangePractice } from "../../redux/features/practice/practiceSlice";
import axiosInstance from "../../utils/axios";
import ChevronDownIcon from "../Icons/ChevronDown";
import {
    getClientSession,
    setClientSession,
} from "../../utils/clientSessionStore";

export default function SideNav() {
    const { isOpen } = useAppSelector((state) => state.nav);

    const { isLoading, data, isSuccess } = useFetchUserDetails();
    const dispatch = useAppDispatch();

    const { logOutUser } = useAuthContext();

    const currentProfile = useCurrentClientProfile();

    const { userId: storedUserId } = getClientSession();
    const userId = currentProfile?.userId || storedUserId || "";

    const { data: userPractices } = useFetchUserPractices(userId);

    const [expandedProfileId, setExpandedProfileId] = useState<string | null>(
        null
    );

    const allProfiles = data?.data
        ? [
              {
                  user_id: data.data.user_id,
                  first_name: data.data.first_name,
                  last_name: data.data.last_name,
                  preferred_name: data.data.preferred_name,
              },
              ...(data.data.sub_profiles || []),
          ]
        : [];

    const { data: expandedProfilePractices, isLoading: isLoadingPractices } =
        useFetchUserPractices(expandedProfileId || userId || "");

    const practices = expandedProfilePractices?.data?.practices;

    const handleToggleProfile = (profileUserId: string) => {
        setExpandedProfileId((prev) =>
            prev === profileUserId ? null : profileUserId
        );
    };

    const handleSelectPractice = (
        profileUserId: string,
        practice: IPractice
    ) => {
        const isSameProfileAndPractice =
            currentProfile?.userId === profileUserId &&
            currentProfile?.practice?.practice_id === practice.practice_id;

        if (isSameProfileAndPractice) {
            return;
        }

        axiosInstance.defaults.headers.common.client_id = practice.client_id;
        axiosInstance.defaults.headers.common.practice_id =
            practice.practice_id;

        setClientSession({
            clientId: practice.client_id,
            userId: profileUserId,
            practice,
        });

        dispatch(
            handleChangePractice({
                practice,
            })
        );
        window.location.href = "/";
    };

    const selectedPractice = userPractices?.data?.practices.find(
        (p) => p.practice_id === currentProfile?.practice?.practice_id
    );
    const consentNeededCount = getSessionsNeedingConsent([
        ...(selectedPractice?.active_sessions ?? []),
        ...(selectedPractice?.held_sessions ?? []),
    ]).length;

    const hasMultipleProfilesOrPractices =
        allProfiles.length > 1 ||
        (allProfiles.length === 1 &&
            expandedProfilePractices?.data?.practices &&
            expandedProfilePractices.data.practices.length > 1);

    let isSmallerScreen = false;

    if (window) {
        isSmallerScreen =
            window && window.matchMedia("(max-width: 768px)").matches;
    }

    const handleSideNavClick = () => {
        if (isSmallerScreen) {
            dispatch(toggleNavState());
        }
    };

    return (
        <>
            {isOpen && (
                <div
                    className="block md:hidden fixed w-full h-full bg-black-main/70 z-[11] overflow-hidden"
                    onClick={() => dispatch(toggleNavState())}
                    role="button"
                    tabIndex={0}
                    onKeyPress={() => dispatch(toggleNavState())}
                    aria-label="close sidebar"
                />
            )}

            <aside
                className={cn(
                    "bg-white h-[100dvh] fixed w-[var(--sidebar-width)] p-24 pb-48 flex flex-col justify-between z-[11] left-0 top-0 border-r border-r-strokedark transform duration-[250ms] ease-out",
                    isOpen ? "translate-x-0" : "-translate-x-full"
                )}
            >
                <div className="flex justify-between items-center gap-x-4">
                    {isLoadingPractices && <Skeleton className="h-48 w-full" />}
                    {userPractices?.data?.practices &&
                        isSuccess &&
                        currentProfile && (
                            <div className="flex-1 min-w-0">
                                {currentProfile.practice && (
                                    <Tooltip
                                        content={
                                            currentProfile.practice
                                                .practice_name
                                        }
                                    >
                                        <div className="bg-white border border-strokelight rounded-r8 p-4 shadow-sm flex items-center gap-8">
                                            <div className="w-32 h-32 rounded-r6 bg-primary text-white flex items-center justify-center text-sm font-semibold shrink-0">
                                                {currentProfile.practice.practice_name
                                                    ?.charAt(0)
                                                    .toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold truncate">
                                                    {
                                                        currentProfile.practice
                                                            .practice_name
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    <Button
                        size="auto"
                        variant="normal"
                        onClick={() => dispatch(toggleNavState())}
                        aria-label="Collapse menu"
                        className="py-8 w-24 shrink-0 flex items-center justify-center rounded-r4 border border-card-bg hover:border-primary"
                    >
                        <svg
                            width="6"
                            height="10"
                            viewBox="0 0 6 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M5 9.00024L1 5.00024L5 1.00024"
                                stroke="#1D596D"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </Button>
                </div>

                <div className="flex-1 pb-40 relative overflow-y-auto overflow-x-hidden">
                    <div className="mb-2 h-auto overflow-y-auto py-4 overflow-x-hidden w-full">
                        <ul className="flex flex-col items-start mt-32">
                            <SideNavLink
                                href="/"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-16 shrink-0"
                                    >
                                        <line x1="8" y1="6" x2="21" y2="6" />
                                        <line x1="8" y1="12" x2="21" y2="12" />
                                        <line x1="8" y1="18" x2="21" y2="18" />
                                        <line x1="3" y1="6" x2="3.01" y2="6" />
                                        <line
                                            x1="3"
                                            y1="12"
                                            x2="3.01"
                                            y2="12"
                                        />
                                        <line
                                            x1="3"
                                            y1="18"
                                            x2="3.01"
                                            y2="18"
                                        />
                                    </svg>
                                }
                                title="Dashboard"
                                onClick={handleSideNavClick}
                            />
                            <SideNavLink
                                href="/billing"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-16 shrink-0"
                                    >
                                        <rect
                                            x="1"
                                            y="4"
                                            width="22"
                                            height="16"
                                            rx="2"
                                            ry="2"
                                        />
                                        <line x1="1" y1="10" x2="23" y2="10" />
                                    </svg>
                                }
                                title="Bills & Payments"
                                onClick={handleSideNavClick}
                                activePaths={["/payment-page"]}
                            />
                            <SideNavLink
                                href="/payment-cards"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-16 shrink-0"
                                    >
                                        <rect
                                            x="2"
                                            y="5"
                                            width="20"
                                            height="14"
                                            rx="2"
                                        />
                                        <line x1="2" y1="10" x2="22" y2="10" />
                                    </svg>
                                }
                                title="Payment Cards"
                                onClick={handleSideNavClick}
                            />
                            <SideNavLink
                                href="/forms"
                                icon={<RecordsIcon />}
                                title="My Forms"
                                onClick={handleSideNavClick}
                                count={consentNeededCount}
                            />
                            {/* <SideNavLink
                                href={`/${practice}/settings`}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="shrink-0 size-16"
                                    >
                                        <circle cx="12" cy="12" r="3" />
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                    </svg>
                                }
                                title="Settings"
                                onClick={handleSideNavClick}
                            /> */}
                            <SideNavLink
                                href="/session-resources"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-16 shrink-0"
                                    >
                                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                    </svg>
                                }
                                title="Session resources"
                                onClick={handleSideNavClick}
                            />
                            <SideNavLink
                                href="/statement-of-account"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-16 shrink-0"
                                    >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                    </svg>
                                }
                                title="Statement of Account"
                                onClick={handleSideNavClick}
                            />
                        </ul>
                    </div>
                </div>

                <div className="mt-20">
                    {isLoading && (
                        <div className=" block md:hidden  py-16">
                            <Skeleton count={3} height={20} width="100%" />
                        </div>
                    )}

                    {data && isSuccess && currentProfile && (
                        <div className="block md:hidden ">
                            <div className="mb-28 w-full h-[1px] bg-strokelight" />

                            {hasMultipleProfilesOrPractices ? (
                                <div className="mb-16">
                                    <div className="flex flex-col gap-y-4">
                                        {allProfiles.map((profile) => {
                                            const isExpanded =
                                                expandedProfileId ===
                                                profile.user_id;
                                            const isSelected =
                                                currentProfile?.userId ===
                                                profile.user_id;

                                            return (
                                                <div key={profile.user_id}>
                                                    <Button
                                                        variant="normal"
                                                        size="auto"
                                                        type="button"
                                                        onClick={() =>
                                                            handleToggleProfile(
                                                                profile.user_id
                                                            )
                                                        }
                                                        className={cn(
                                                            "w-full flex items-center justify-between px-12 py-10 rounded-r8 transition-colors",
                                                            isSelected
                                                                ? "bg-blue-100"
                                                                : "hover:bg-strokelight"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-12">
                                                            <span className="flex items-center justify-center bg-secondary-light w-32 h-32 rounded-full border-1.5 border-primary text-[1.28rem] font-medium">
                                                                {getClientInitials(
                                                                    profile.preferred_name,
                                                                    profile.first_name,
                                                                    profile.last_name
                                                                )}
                                                            </span>
                                                            <span className="capitalize text-sm font-semibold">
                                                                {getClientDisplayName(
                                                                    profile.preferred_name,
                                                                    profile.first_name,
                                                                    profile.last_name,
                                                                    "full_name"
                                                                )}
                                                            </span>
                                                        </div>
                                                        <ChevronDownIcon
                                                            stroke="#0b132b"
                                                            className={cn(
                                                                "w-16 h-16 transition-transform",
                                                                isExpanded &&
                                                                    "rotate-180"
                                                            )}
                                                        />
                                                    </Button>

                                                    {isExpanded && (
                                                        <div className="ml-16 mt-8 mb-8 border-l-2 border-l-strokelight pl-12">
                                                            {isLoadingPractices && (
                                                                <Skeleton className="h-32 w-full" />
                                                            )}
                                                            {!isLoadingPractices &&
                                                                practices &&
                                                                practices.length >
                                                                    0 && (
                                                                    <div className="flex flex-col gap-y-6">
                                                                        {practices?.map(
                                                                            (
                                                                                practice
                                                                            ) => {
                                                                                const practiceSelected =
                                                                                    currentProfile
                                                                                        ?.practice
                                                                                        ?.practice_id ===
                                                                                        practice.practice_id &&
                                                                                    currentProfile?.userId ===
                                                                                        profile.user_id;

                                                                                const activeSessions =
                                                                                    practice.active_sessions ??
                                                                                    [];

                                                                                const activeProviderNames =
                                                                                    Array.from(
                                                                                        new Set(
                                                                                            activeSessions?.map(
                                                                                                (
                                                                                                    session
                                                                                                ) =>
                                                                                                    `${session.provider.first_name} ${session.provider.last_name}`
                                                                                            )
                                                                                        )
                                                                                    ).join(
                                                                                        ", "
                                                                                    );

                                                                                return (
                                                                                    <Button
                                                                                        variant="normal"
                                                                                        size="auto"
                                                                                        key={
                                                                                            practice.practice_id
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleSelectPractice(
                                                                                                profile.user_id,
                                                                                                practice
                                                                                            )
                                                                                        }
                                                                                        className={cn(
                                                                                            "w-full flex items-center justify-between px-12 py-10 rounded-r8 transition-colors",
                                                                                            practiceSelected
                                                                                                ? "bg-blue-100"
                                                                                                : "hover:bg-strokelight"
                                                                                        )}
                                                                                    >
                                                                                        <div className="flex items-center gap-12">
                                                                                            <div className="w-28 h-28 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-xs shrink-0">
                                                                                                {practice.practice_name
                                                                                                    ?.charAt(
                                                                                                        0
                                                                                                    )
                                                                                                    .toUpperCase()}
                                                                                            </div>
                                                                                            <div className="flex flex-col items-start">
                                                                                                <span className="text-xs font-medium">
                                                                                                    {
                                                                                                        practice.practice_name
                                                                                                    }
                                                                                                </span>
                                                                                                {practice
                                                                                                    .active_sessions
                                                                                                    ?.length >
                                                                                                    0 && (
                                                                                                    <span className="text-xs text-gray">
                                                                                                        {truncateString(
                                                                                                            activeProviderNames,
                                                                                                            30
                                                                                                        )}
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                        {practiceSelected && (
                                                                                            <svg
                                                                                                className="w-16 h-16 text-primary shrink-0"
                                                                                                fill="currentColor"
                                                                                                viewBox="0 0 20 20"
                                                                                            >
                                                                                                <path
                                                                                                    fillRule="evenodd"
                                                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                                                    clipRule="evenodd"
                                                                                                />
                                                                                            </svg>
                                                                                        )}
                                                                                    </Button>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </div>
                                                                )}
                                                            {!isLoadingPractices &&
                                                                practices &&
                                                                practices?.length ===
                                                                    0 && (
                                                                    <div className="text-xs text-gray py-8 pl-12">
                                                                        No
                                                                        practices
                                                                        available
                                                                    </div>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-8 mb-24">
                                    <Avatar
                                        label={getClientInitials(
                                            currentProfile.preferredName,
                                            currentProfile.firstName,
                                            currentProfile.lastName
                                        )}
                                    />
                                    <div className="font-medium text-base capitalize">
                                        {getClientDisplayName(
                                            currentProfile.preferredName,
                                            currentProfile.firstName,
                                            currentProfile.lastName,
                                            "full_name"
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="pt-16 border-t border-t-strokelight">
                                <Button
                                    className="font-medium text-sm text-gray hover:text-black"
                                    variant="normal"
                                    size="auto"
                                    onClick={logOutUser}
                                >
                                    Logout
                                </Button>
                            </div>
                        </div>
                    )}
                    <img
                        src={patternBg}
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-0 left-0 right-[62px] w-[194px] h-[165px] -z-[1]"
                    />
                </div>
            </aside>
        </>
    );
}
